import React, { useCallback, useEffect, useState } from "react";
import useCallCenterT from "../../hooks/useCallCenterT";
import {
  LISTING_CARD_CLASS,
  LISTING_TABLE_HEAD_CLASS,
  LISTING_TABLE_HEADER_CELL_CLASS,
  LISTING_TABLE_ROW_CLASS,
} from "./listing/listingStyles";

const LOCKED_MANDATORY_KEYS = new Set(["accountNo", "mobileNumber", "agentName", "priority"]);

const CHECKBOX_CLASS =
  "h-4 w-4 rounded border-slate-300 text-[#2B7FFF] accent-[#2B7FFF] focus:ring-[#2B7FFF] disabled:opacity-60";

const TicketColumnConfigPanel = ({ columns = [], onSave, saving = false, saveControlRef }) => {
  const t = useCallCenterT();
  const [draftColumns, setDraftColumns] = useState([]);

  useEffect(() => {
    setDraftColumns(Array.isArray(columns) ? columns.map((column) => ({ ...column })) : []);
  }, [columns]);

  const updateColumn = (fieldKey, patch) => {
    setDraftColumns((current) =>
      current.map((column) => (column.fieldKey === fieldKey ? { ...column, ...patch } : column))
    );
  };

  const handleSave = useCallback(() => {
    onSave(
      draftColumns.map((column, index) => ({
        fieldKey: column.fieldKey,
        displayLabel: column.displayLabel,
        predefined: column.predefined,
        mandatory: column.mandatory,
        visibleInList: column.visibleInList,
        visibleInForm: column.visibleInForm,
        visibleInUpdate: column.visibleInUpdate,
        visibleInFilter: column.visibleInFilter,
        displayOrder: column.displayOrder ?? index + 1,
        dataType: column.dataType || "text",
        uploadOnly: column.uploadOnly,
      }))
    );
  }, [draftColumns, onSave]);

  useEffect(() => {
    if (!saveControlRef) return undefined;
    saveControlRef.current = handleSave;
    return () => {
      saveControlRef.current = null;
    };
  }, [handleSave, saveControlRef]);

  if (draftColumns.length === 0) {
    return null;
  }

  return (
    <div className={`${LISTING_CARD_CLASS} overflow-hidden`}>
      <div className="border-b border-slate-100 px-6 py-4">
        <h3 className="text-base font-semibold text-slate-900">{t("ccColumnConfigTitle")}</h3>
        <p className="mt-1 text-sm text-slate-500">{t("ccColumnConfigSubtitle")}</p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className={LISTING_TABLE_HEAD_CLASS}>
            <tr>
              <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccColumnLabel")}</th>
              <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccColumnType")}</th>
              <th className={`${LISTING_TABLE_HEADER_CELL_CLASS} text-center`}>{t("ccMandatory")}</th>
              <th className={`${LISTING_TABLE_HEADER_CELL_CLASS} text-center`}>{t("ccShowInList")}</th>
              <th className={`${LISTING_TABLE_HEADER_CELL_CLASS} text-center`}>{t("ccShowInForm")}</th>
              <th className={`${LISTING_TABLE_HEADER_CELL_CLASS} text-center`}>{t("ccShowInUpdate")}</th>
              <th className={`${LISTING_TABLE_HEADER_CELL_CLASS} text-center`}>{t("ccShowInFilter")}</th>
            </tr>
          </thead>
          <tbody>
            {draftColumns.map((column) => (
              <tr key={column.fieldKey} className={LISTING_TABLE_ROW_CLASS}>
                <td className="px-6 py-3">
                  <div className="font-medium text-slate-800">{column.displayLabel}</div>
                  <div className="font-mono text-[11px] text-slate-400">{column.fieldKey}</div>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex rounded-md px-2.5 py-1 text-[11px] font-semibold ${
                      column.predefined ? "bg-[#EEF4FF] text-[#236DB4]" : "bg-emerald-50 text-emerald-700"
                    }`}
                  >
                    {column.predefined ? t("ccPredefinedColumn") : t("ccCustomColumn")}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={Boolean(column.mandatory)}
                    disabled={saving || LOCKED_MANDATORY_KEYS.has(column.fieldKey)}
                    onChange={(event) => updateColumn(column.fieldKey, { mandatory: event.target.checked })}
                    className={CHECKBOX_CLASS}
                    aria-label={`${t("ccMandatory")} — ${column.displayLabel}`}
                  />
                </td>
                <td className="px-4 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={Boolean(column.visibleInList)}
                    disabled={saving}
                    onChange={(event) => updateColumn(column.fieldKey, { visibleInList: event.target.checked })}
                    className={CHECKBOX_CLASS}
                    aria-label={`${t("ccShowInList")} — ${column.displayLabel}`}
                  />
                </td>
                <td className="px-4 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={Boolean(column.visibleInForm)}
                    disabled={saving || column.uploadOnly}
                    onChange={(event) => updateColumn(column.fieldKey, { visibleInForm: event.target.checked })}
                    className={CHECKBOX_CLASS}
                    aria-label={`${t("ccShowInForm")} — ${column.displayLabel}`}
                  />
                </td>
                <td className="px-4 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={Boolean(column.visibleInUpdate)}
                    disabled={saving || column.uploadOnly}
                    onChange={(event) => updateColumn(column.fieldKey, { visibleInUpdate: event.target.checked })}
                    className={CHECKBOX_CLASS}
                    aria-label={`${t("ccShowInUpdate")} — ${column.displayLabel}`}
                  />
                </td>
                <td className="px-4 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={Boolean(column.visibleInFilter)}
                    disabled={saving || column.uploadOnly}
                    onChange={(event) => updateColumn(column.fieldKey, { visibleInFilter: event.target.checked })}
                    className={CHECKBOX_CLASS}
                    aria-label={`${t("ccShowInFilter")} — ${column.displayLabel}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TicketColumnConfigPanel;
